import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { differenceInDays, format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import GroupRemoveIcon from '@mui/icons-material/GroupRemove';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { CrewDTO, CREW_TIER_LIMITS, Profile } from '../../../../shared/types';
import { useCountProfilesByCrew, useCrewProfiles, useOffboardProfile } from '../../hooks/useProfiles';
import { useCrewOutstandingInvoice, useCrewUpgradeSubscription } from '../../hooks/useSubscriptions';
import { useCurrentCrewMember } from '../../hooks/useCurrentCrewMember';
import { crewKeys } from '../../hooks/useCrews';
import { PageContainer } from '../PageContainer';
import Container from '@mui/material/Container/Container';

interface SuspendedCrewRecoveryProps {
    crew: CrewDTO;
}

export const SuspendedCrewRecovery: React.FC<SuspendedCrewRecoveryProps> = ({ crew }) => {
    const queryClient = useQueryClient();
    const isArchived = crew.status === 'archived';
    const freeMax = CREW_TIER_LIMITS.free.maxMembers;

    // Member count
    const { data: countData } = useCountProfilesByCrew(crew.slug, true);
    const memberCount = countData?.data?.count ?? null;

    // Outstanding invoice check
    const { data: invoiceData, isLoading: invoiceLoading } = useCrewOutstandingInvoice(crew.slug);
    const invoiceInfo = invoiceData?.data;

    // Pro upgrade
    const upgradeMutation = useCrewUpgradeSubscription(crew.slug);
    const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [upgradeError, setUpgradeError] = useState<string | null>(null);
    const [upgradeSuccess, setUpgradeSuccess] = useState(false);

    const pro = CREW_TIER_LIMITS.pro;
    const ppmDollars = (pro.ppm / 100).toFixed(2);
    const minDollars = (pro.minMonthlyCost / 100).toFixed(2);

    // Countdown / archive date
    const daysRemaining = crew.suspendedAt
        ? Math.max(0, 30 - differenceInDays(new Date(), new Date(crew.suspendedAt)))
        : null;
    const archivedOn = crew.suspendedAt
        ? format(new Date(new Date(crew.suspendedAt).getTime() + 30 * 24 * 60 * 60 * 1000), 'MMMM d, yyyy')
        : null;

    // Inline member retention (archived crews only - profiles page is inaccessible)
    const { data: profilesData, isLoading: profilesLoading } = useCrewProfiles(isArchived ? crew.slug : '');
    const allProfiles: Profile[] = profilesData?.data ?? [];
    const { data: myProfile } = useCurrentCrewMember(crew.slug);
    const offboardMutation = useOffboardProfile(crew.slug);
    const [keptProfiles, setKeptProfiles] = useState<Profile[]>([]);
    const [offboardError, setOffboardError] = useState<string | null>(null);
    const [offboardSuccess, setOffboardSuccess] = useState(false);
    const [offboarding, setOffboarding] = useState(false);

    // Once profiles load, pre-populate selection with up to freeMax profiles (own profile always first)
    const [selectionInitialized, setSelectionInitialized] = useState(false);
    if (!selectionInitialized && allProfiles.length > 0 && myProfile) {
        const others = allProfiles.filter(p => p._id !== myProfile._id).slice(0, freeMax - 1);
        setKeptProfiles([myProfile, ...others]);
        setSelectionInitialized(true);
    }

    const profilesToOffboard = allProfiles.filter(p => !keptProfiles.some(k => k._id === p._id));

    const handleKeepChange = (_: React.SyntheticEvent, value: Profile[]) => {
        // Always keep own profile
        const withSelf = myProfile && !value.some(p => p._id === myProfile._id)
            ? [myProfile, ...value]
            : value;
        // Enforce max freeMax
        setKeptProfiles(withSelf.slice(0, freeMax));
    };

    const handleOffboardConfirm = async () => {
        if (profilesToOffboard.length === 0) return;
        setOffboarding(true);
        setOffboardError(null);
        try {
            for (const profile of profilesToOffboard) {
                await offboardMutation.mutateAsync({ profileId: profile._id });
            }
            setOffboardSuccess(true);
            queryClient.invalidateQueries({ queryKey: crewKeys.detail(crew.slug) });
            queryClient.invalidateQueries({ queryKey: ['profiles', 'all', crew.slug] });
            queryClient.invalidateQueries({ queryKey: ['profiles', 'count', crew.slug] });
        } catch (err: any) {
            setOffboardError(err?.response?.data?.error || err?.message || 'Failed to offboard some members');
        } finally {
            setOffboarding(false);
        }
    };

    const handleUpgradeOpen = () => {
        setAgreed(false);
        setUpgradeError(null);
        setUpgradeDialogOpen(true);
    };

    const handleUpgradeActivate = async () => {
        try {
            setUpgradeError(null);
            await upgradeMutation.mutateAsync('pro');
            setUpgradeDialogOpen(false);
            setUpgradeSuccess(true);
            // Refetch crew data so the banner disappears
            queryClient.invalidateQueries({ queryKey: crewKeys.detail(crew.slug) });
        } catch (err: any) {
            setUpgradeError(err?.response?.data?.error || err?.message || 'Failed to activate subscription');
        }
    };

    const membersToRemove = memberCount !== null ? Math.max(0, memberCount - freeMax) : null;

    const renderUpgradeDialog = () => (
        <Dialog open={upgradeDialogOpen} onClose={() => setUpgradeDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Upgrade to Pro</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1 }}>
                        <Typography variant="h6">${ppmDollars} / user / month</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Minimum ${minDollars}/month · Billed monthly
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>How billing works</Typography>
                        <List dense disablePadding>
                            <ListItem disableGutters>
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                    <CheckIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Invoiced at the end of each billing period"
                                    secondary="No upfront payment required"
                                />
                            </ListItem>
                            <ListItem disableGutters>
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                    <CheckIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Pay via Stripe's secure hosted invoice page"
                                    secondary="We never store your payment details"
                                />
                            </ListItem>
                            <ListItem disableGutters>
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                    <CheckIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Members added or removed mid-month are prorated"
                                    secondary="You only pay for the days each member was active"
                                />
                            </ListItem>
                        </List>
                    </Box>

                    <Divider />

                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Example
                        </Typography>
                        <Typography variant="body2">
                            20 members for a full month = 20 × ${ppmDollars} = <strong>${(pro.ppm * 20 / 100).toFixed(2)}/mo</strong>
                            {pro.ppm * 20 < pro.minMonthlyCost
                                ? ` → minimum of $${minDollars} applies`
                                : ` (above the $${minDollars} minimum)`}
                        </Typography>
                    </Box>

                    {upgradeError && (
                        <Alert severity="error">{upgradeError}</Alert>
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={agreed}
                                onChange={(e) => setAgreed(e.target.checked)}
                            />
                        }
                        label={
                            <Typography variant="body2">
                                I agree to the billing terms.
                            </Typography>
                        }
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setUpgradeDialogOpen(false)} disabled={upgradeMutation.isPending}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleUpgradeActivate}
                    disabled={!agreed || upgradeMutation.isPending}
                >
                    {upgradeMutation.isPending ? 'Activating...' : 'Activate Pro'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // Suspended state: compact banner above the normal crew content
    if (!isArchived) {
        return (
            <Container disableGutters sx={{ mb: 2 }}>
                <Alert
                    severity="warning"
                    sx={{ borderRadius: 0 }}
                    action={
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                color="inherit"
                                component={Link}
                                to={`/c/${crew.slug}/profiles`}
                            >
                                Manage Members
                            </Button>
                            {invoiceLoading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : invoiceInfo?.hasOutstandingInvoice ? (
                                invoiceInfo.invoiceUrl ? (
                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        endIcon={<OpenInNewIcon fontSize="inherit" />}
                                        onClick={() => window.open(invoiceInfo.invoiceUrl!, '_blank', 'noopener,noreferrer')}
                                    >
                                        Pay Invoice
                                    </Button>
                                ) : null
                            ) : (
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={handleUpgradeOpen}
                                >
                                    Upgrade
                                </Button>
                            )}
                        </Stack>
                    }
                >
                    <AlertTitle>Overcrowded</AlertTitle>
                    Your crew has <strong>{memberCount ?? '…'}</strong> members, exceeding the Free plan
                    limit of <strong>{freeMax}</strong>. Please remove members or upgrade to Pro to keep services.

                    {invoiceInfo?.hasOutstandingInvoice && (
                        <> You have an outstanding invoice of{' '}
                            <strong>${invoiceInfo.amountDue != null ? (invoiceInfo.amountDue / 100).toFixed(2) : '-'}</strong>{' '}
                            that must be paid before upgrading.
                        </>
                    )}
                    {upgradeSuccess && (
                        <> Your crew is now on the Pro plan and has been reactivated.</>
                    )}
                </Alert>

                {/* Upgrade dialog - still available from the banner button */}
                {renderUpgradeDialog()}
            </Container>
        );
    }

    // Archived state: full recovery panel (Outlet is hidden, so inline tools are needed)
    return (
        <PageContainer spacing={2} sx={{ py: 2 }}>
            {/* Top banner */}
            <Alert severity="error" sx={{ borderRadius: 0 }}>
                <AlertTitle>Crew Archived</AlertTitle>
                Your crew was archived on <strong>{archivedOn}</strong> because the crew exceeded the Free plan member limit of {freeMax}.
                Resolve one of the options below to restore access.
            </Alert>

            {upgradeSuccess && (
                <Alert severity="success" sx={{ borderRadius: 0 }}>
                    Your crew is now on the Pro plan and has been reactivated.
                    You'll receive your first invoice at the end of this billing period.
                </Alert>
            )}

            {offboardSuccess && (
                <Alert severity="success" sx={{ borderRadius: 0 }}>
                    Members removed. Your crew is being reactivated.
                </Alert>
            )}

            {/* Two recovery options */}
            <Box sx={{ p: 0 }}>
                <Grid container spacing={3}>
                    {/* Option A: Remove members */}
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <GroupRemoveIcon color="action" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Remove Members</Typography>
                                </Box>

                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Reduce your crew to {freeMax} members or fewer to restore free plan access automatically.
                                </Typography>

                                {memberCount !== null && (
                                    <Box sx={{ my: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                        <Typography variant="body2">
                                            <strong>{memberCount}</strong> members · Free plan allows <strong>{freeMax}</strong>
                                        </Typography>
                                        {membersToRemove !== null && membersToRemove > 0 && (
                                            <Typography variant="body2" color="error.main" mt={0.5}>
                                                Remove at least <strong>{membersToRemove}</strong> member{membersToRemove !== 1 ? 's' : ''} to reactivate.
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {/* Profiles page is inaccessible - inline retention selector */}
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        Select up to {freeMax} members to keep. Everyone else will be offboarded.
                                        Your own profile is always retained.
                                    </Typography>

                                    {profilesLoading ? (
                                        <Box display="flex" justifyContent="center" my={2}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : (
                                        <Autocomplete
                                            multiple
                                            options={allProfiles}
                                            value={keptProfiles}
                                            onChange={handleKeepChange}
                                            getOptionLabel={(p) => p.displayName || p.handle || p._id}
                                            isOptionEqualToValue={(a, b) => a._id === b._id}
                                            getOptionDisabled={(p) => !!myProfile && p._id === myProfile._id}
                                            renderOption={(props, p) => (
                                                <li {...props} key={p._id}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Avatar
                                                            src={p.profilePicture ?? undefined}
                                                            sx={{ width: 24, height: 24, fontSize: 12 }}
                                                        >
                                                            {(p.displayName || p.handle || '?')[0].toUpperCase()}
                                                        </Avatar>
                                                        <span>{p.displayName || p.handle}</span>
                                                        {myProfile && p._id === myProfile._id && (
                                                            <LockIcon sx={{ fontSize: 14, color: 'text.disabled', ml: 0.5 }} />
                                                        )}
                                                    </Box>
                                                </li>
                                            )}
                                            renderTags={(value, getTagProps) =>
                                                value.map((p, index) => {
                                                    const isMe = !!myProfile && p._id === myProfile._id;
                                                    return (
                                                        <Chip
                                                            {...getTagProps({ index })}
                                                            key={p._id}
                                                            label={p.displayName || p.handle}
                                                            avatar={
                                                                <Avatar src={p.profilePicture ?? undefined}>
                                                                    {(p.displayName || p.handle || '?')[0].toUpperCase()}
                                                                </Avatar>
                                                            }
                                                            onDelete={isMe ? undefined : getTagProps({ index }).onDelete}
                                                            size="small"
                                                            sx={isMe ? { pr: 1 } : undefined}
                                                        />
                                                    );
                                                })
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={`Members to keep (${keptProfiles.length}/${freeMax})`}
                                                    placeholder={keptProfiles.length < freeMax ? 'Add member…' : ''}
                                                    size="small"
                                                />
                                            )}
                                            disableCloseOnSelect
                                            sx={{ mb: 2 }}
                                        />
                                    )}

                                    {offboardError && (
                                        <Alert severity="error" sx={{ mb: 1.5 }}>{offboardError}</Alert>
                                    )}

                                    <Button
                                        variant="contained"
                                        color="error"
                                        fullWidth
                                        disabled={profilesToOffboard.length === 0 || offboarding || offboardSuccess}
                                        onClick={handleOffboardConfirm}
                                    >
                                        {offboarding
                                            ? `Removing ${profilesToOffboard.length} member${profilesToOffboard.length !== 1 ? 's' : ''}…`
                                            : `Remove ${profilesToOffboard.length} member${profilesToOffboard.length !== 1 ? 's' : ''} & Reactivate`}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Option B: Reactivate subscription */}
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: '100%', borderColor: 'primary.main' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <StarIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Reactivate Subscription</Typography>
                                </Box>

                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Upgrade to Pro to restore unlimited members and all Pro features.
                                </Typography>

                                {invoiceLoading ? (
                                    <Box display="flex" justifyContent="center" my={3}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : invoiceInfo?.hasOutstandingInvoice ? (
                                    <Box sx={{ my: 2 }}>
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            You have an outstanding invoice of{' '}
                                            <strong>
                                                ${invoiceInfo.amountDue != null
                                                    ? (invoiceInfo.amountDue / 100).toFixed(2)
                                                    : '-'}
                                            </strong>{' '}
                                            that must be paid before you can upgrade.
                                        </Alert>
                                        {invoiceInfo.invoiceUrl && (
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                                endIcon={<OpenInNewIcon />}
                                                onClick={() => window.open(invoiceInfo.invoiceUrl!, '_blank', 'noopener,noreferrer')}
                                            >
                                                Pay Invoice
                                            </Button>
                                        )}
                                        <Typography variant="caption" color="text.secondary" display="block" mt={1} textAlign="center">
                                            After paying, return here to upgrade.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ my: 2 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            No outstanding invoices. You can upgrade immediately.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            onClick={handleUpgradeOpen}
                                        >
                                            Upgrade to Pro
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {renderUpgradeDialog()}
        </PageContainer>
    );
};
